import {
  authenticatedFetch,
  extractApiErrorMessage,
  resolvePeriverseApiBaseUrl,
} from '../shared';
import {
  CaseTypeDtoSchema,
  CaseTypesFilterParams,
  CreateCaseTypeDto,
  PaginatedCaseTypesResponseDto,
  PaginatedCaseTypesResponseDtoSchema,
  UpdateCaseTypeDto,
  type CaseTypeDto,
} from '@peritasai/types';

type BaseUrlResolver = () => string;

export class CaseTypesApiClient {
  constructor(
    private readonly resolveBaseUrl: BaseUrlResolver = resolvePeriverseApiBaseUrl
  ) {}

  async getCaseTypes(
    options: CaseTypesFilterParams = {}
  ): Promise<PaginatedCaseTypesResponseDto> {
    const queryParams = new URLSearchParams();

    if (options.search) {
      queryParams.append('search', options.search);
    }

    const page = options.page !== undefined ? options.page + 1 : 1;
    const limit = options.limit ?? 10;
    queryParams.append('page', page.toString());
    queryParams.append('limit', limit.toString());

    const url = `${this.resolveBaseUrl()}/case-types?${queryParams.toString()}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: { accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    return PaginatedCaseTypesResponseDtoSchema.parse(await response.json());
  }

  async createCaseType(dto: CreateCaseTypeDto): Promise<CaseTypeDto> {
    const url = `${this.resolveBaseUrl()}/case-types`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(dto),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    return CaseTypeDtoSchema.parse(await response.json());
  }

  async updateCaseType(id: string, dto: UpdateCaseTypeDto): Promise<CaseTypeDto> {
    const url = `${this.resolveBaseUrl()}/case-types/${id}`;

    const response = await authenticatedFetch(url, {
      method: 'PATCH',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(dto),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    return CaseTypeDtoSchema.parse(await response.json());
  }
}

export const createCaseTypesApiClient = (
  resolveBaseUrl: BaseUrlResolver = resolvePeriverseApiBaseUrl
): CaseTypesApiClient => new CaseTypesApiClient(resolveBaseUrl);

export const caseTypesApiClient = new CaseTypesApiClient();

export type { BaseUrlResolver };
